import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {NestedTreeControl} from '@angular/cdk/tree';
import {Observable, of, Subscription} from 'rxjs';
import {FileClickEmitter, FileNode} from './clickatell-tree.interface';
import * as filteringTree from './filteringTree';
import {SharedService} from '../../../core/services/store/shared-store/shared-storage.service';
import {MatDialog} from '@angular/material/dialog';
import {MatMenuTrigger} from '@angular/material/menu';
import {UserDetails} from '../../models/userDetails';
import {FlowUserDetails} from '../../models/FlowUserDetails';
import {map, reduce, switchMap, tap} from "rxjs/operators";
import {flatMap} from 'rxjs/internal/operators';
import find from 'lodash/find';
import {FlowManagerService} from '../../../modules/flow/services/flow-manager.service';
import {FlowManagerUtil} from '../../utils/flow-manager-util';
import {CopyFlowComponent} from '../../../modules/flow/components/copy-flow/copy-flow.component';
import {RenameFlowComponent} from '../../../modules/flow/components/rename-flow/rename-flow.component';
import {MoveFlowComponent} from '../../../modules/flow/components/move-flow/move-flow.component';
import {DeleteFlowComponent} from '../../../modules/flow/components/delete-flow/delete-flow.component';
import {RolesService} from '../../../core/services/roles/roles.service';
import {BotModalComponent} from '../../../modules/flow/components/bots/bot-modal/bot-modal.component';
import {RenameTemplateComponent} from "../../../modules/flow/components/bots/rename-template/rename-template.component";
import {swalMini} from '../../utils/utils';
import {SubmissionService} from '../../../core/services/management/submission/submission.service';
import {SubmissionRequest} from '../../models';
import {StateService} from "../../../core/services/store/state/state.service";
import {FlowModeState, FlowType} from "../../models/state/Flow";

@Component({
  selector: 'clickatell-tree',
  templateUrl: './clickatell-tree.component.html',
  styleUrls: ['./clickatell-tree.component.scss'],
})
export class ClickatellTreeComponent implements OnChanges, DoCheck, OnDestroy, OnInit {

  @Input() treeData$: Observable<Array<FileNode>>;
  @Input() searchValue: string;
  @Input() botId: string;
  @Input() lockedFlows: {} = {};
  @Input() isModal: boolean;
  @Input() dynamicApiRestriction = false;
  @Input() disableFlowFilter = false;
  @Output() onFileClick: EventEmitter<FileClickEmitter> = new EventEmitter();
  @Output() loadFlow: any = new EventEmitter();
  @Output() newFlow: any = new EventEmitter();

  @ViewChild('botContextMenuBtn', {read: MatMenuTrigger, static: false}) botContextMenu: MatMenuTrigger;
  @ViewChild('contextMenuBtn', {read: MatMenuTrigger, static: false}) contextMenu: MatMenuTrigger;
  @ViewChild('tree') tree;
  @ViewChild('submitForApprovalDialog', { static: true }) submitForApprovalDialog: TemplateRef<any>;

  contextMenuPosition = { x: '0px', y: '0px' };
  botContextMenuPosition = { x: '0px', y: 'opx' };
  selectedMenuData;
  selectedBotMenuData: any = {};
  submitForApprovalMessage = '';
  treeControl = new NestedTreeControl<FileNode>((node) => node.children);
  _dataSource: Observable<FileNode[]> = null;

  isFlowLocked: Boolean = false;
  treeSubscription: Subscription;

  flowsMetadata: any;
  selectedFlowInModal: any;

  constructor(
    public sharedService: SharedService,
    private stateService: StateService,
    public dialog: MatDialog,
    public rolesService: RolesService,
    private flowManagerService: FlowManagerService,
    private submissionService: SubmissionService,
  ) {}

  hasChild = (_: number, node: FileNode) => !!node.children && node.children.length > 0;

  ngOnDestroy(): void {
    if (this.treeSubscription) {
      this.treeSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.flowManagerService.getFlowMetaData(true)
        .pipe(
            map(value => {
              if (this.shouldFilterForApiFlow()) {
                return value.filter(val => val.flowType === FlowType.ApiFlow);
              }
            }),
            tap(flowMetadata => this.flowsMetadata = flowMetadata)
        )
        .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.treeData$ && this.treeData$) {
      const flowsUpdated = this.sharedService.getFlowsUpdated();
      const firstLoad = changes.treeData$.isFirstChange();
      if (flowsUpdated || firstLoad) {
        this.refreshTreeData();
      }
      if (flowsUpdated && firstLoad === false) {
        this.refreshTreeData(!flowsUpdated);
      }
    }
    if (changes.searchValue && changes.searchValue.currentValue && this.tree) {
      this.treeControl.expandAll();
    } else if ((!changes.searchValue || !changes.searchValue.currentValue) && (!this.botId || this.botId === 'undefined') && this.tree) {
      this.treeControl.collapseAll();
    }
  }

  ngDoCheck(): void {
    if (this.selectedMenuData) {
      if (this.isFlowLocked !== this.lockedFlows.hasOwnProperty(this.selectedMenuData.flowId)) {
        this.contextMenu.closeMenu();
        this.botContextMenu.closeMenu();
        this.isFlowLocked = this.lockedFlows.hasOwnProperty(this.selectedMenuData.flowId);
      }
    }
  }

  refreshTreeData(expandTreeView ?: boolean) {
    if (this.treeData$) {
      this.treeData$.pipe(
          flatMap((fileNode: FileNode[]) => {
            return fileNode;
          }),
          map((fileNode: FileNode) => {
            return this.updateFileNode(fileNode, 1);
          }),
          reduce((acc, x) => {
            acc.push(x);
            return acc;
          }, []),
          switchMap((finalData: FileNode[]) => {
            if (this.shouldFilterForApiFlow()) {
              return this._dataSource = filteringTree.filterAndSortTree(of(finalData), '', FlowType.ApiFlow);
            }
            return this._dataSource = filteringTree.filterAndSortTree(of(finalData));
          }),
          tap((fileNodes: FileNode[]) => this.treeControl.dataNodes = fileNodes),
          tap( () => this.sharedService.setFlowsUpdated(false)),
          tap( () => expandTreeView ? this.treeControl.expandAll(): this.treeControl.collapseAll())
      ).subscribe();
    }
  }

  private updateFileNode(fileNode: FileNode, level: number): any {
    const newFile = [];
    if (fileNode.children) {
      const newLevel = level + 1;
      for (const file of fileNode.children) {
        newFile.push(this.updateFileNode(file, newLevel));
      }
      fileNode.children = newFile;
      return { ...fileNode, level: level };
    }

    return { ...fileNode, level: level };
  }

  openFile(node: FileNode) {
    const index = node.path.lastIndexOf('/');
    const modifiedPath = index === -1 ? '' : node.path.slice(0, index);
    const newPath = modifiedPath === '' ? node.id : modifiedPath + '/' + node.id;

    const emit: FileClickEmitter = {
      name: node.id,
      path: newPath,
      title: node.name,
      additionalData: node.additionalData
    };
    this.selectedFlowInModal = node;
    this.onFileClick.emit(emit);
  }

  searchMark(name: string) {
    if (this.searchValue && this.searchValue !== '') {
      const searchLength = this.searchValue.length;
      const searchIndex = name.toLowerCase().indexOf(this.searchValue.toLowerCase());
      const indexValue = name.substring(searchIndex, searchIndex + searchLength);

      return name
        .toLowerCase()
        .replace(this.searchValue.toLowerCase(), '<mark>' + indexValue + '</mark>');
    }

    return name;
  }

  onBotContextMenu(event, folder: any) {
    if (this.sharedService.getEnvironment() !== 'prod' && this.rolesService.canEditFlows()) {
      event.preventDefault();
      this.selectedBotMenuData = folder;
      this.botContextMenuPosition.x = event.clientX + 'px';
      this.botContextMenuPosition.y = event.clientY + 'px';
      this.openBotContextMenu();
    }
  }

  private openBotContextMenu() {
    if (this.rolesService.canEditFlows()) {
      this.botContextMenu.openMenu();
    }
  }

  onContextMenu(event, node: FileNode) {
    if (this.sharedService.getEnvironment() !== 'prod' && this.rolesService.canEditFlows()) {
      event.preventDefault();
      const flowName = FlowManagerUtil.sanitizeFlowName(node.name);
      const path = FlowManagerUtil.convertToPathWithoutFlowId(node.path);
      this.selectedMenuData = {
        flowId: node.id,
        path: path,
        flowName: flowName,
      };
      this.contextMenuPosition.x = event.clientX + 'px';
      this.contextMenuPosition.y = event.clientY + 'px';
      this.openContextMenu(node.additionalData);
    }
  }

  private openContextMenu(additionalData: any) {
    const editOnly = additionalData['editOnly'];
    this.contextMenu.menuData = {
      disable:
        this.lockedFlows.hasOwnProperty(this.selectedMenuData.flowId) &&
        !this.myLockFlow(this.selectedMenuData.flowId),
      editOnly: editOnly ? editOnly : false
    };
    if (this.rolesService.canEditFlows()) {
      this.contextMenu.openMenu();
    }
  }

  onDuplicate() {
    this.dialog.open(CopyFlowComponent, {
      panelClass: 'saveNewFlowDialog',
       width: '525px',
      height: '407px',
      data: this.selectedMenuData,
    });
  }

  onRename() {
    if(this.sharedService.getEntityType()!=='TEMPLATE') {
      this.dialog.open(RenameFlowComponent, {
        panelClass: 'renameFlowDialog',
        width: '525px',
        height: '314px',
        data: this.selectedMenuData,
      });
    } else {
      this.dialog.open(RenameTemplateComponent, {
        panelClass: 'renameTemplateDialog',
        width: '442px',
        height: '273px',
        data: this.selectedMenuData,
      });
    }
  }

  onMove() {
    this.dialog.open(MoveFlowComponent, {
      panelClass: 'moveFlowDialog',
       width: '525px',
      height: '314px',
      data: this.selectedMenuData,
    });
  }

  onRemove() {
    this.dialog.open(DeleteFlowComponent, {
      panelClass: 'deleteFlowDialog',
      data: this.selectedMenuData,
      backdropClass: 'delete-flow-backdrop',
    });
  }

  onEditBot() {
    this.dialog.open(BotModalComponent, {
      panelClass: 'editBotDialog',
      width: '713px',
      height: '640px',
      data: {isEdit: true , botData: this.selectedBotMenuData},
      backdropClass: 'eidt-bot-backdrop',
    });
  }

  showSubmitForApprovalDialog() {
    const dialogRef = this.dialog.open(this.submitForApprovalDialog, {
      panelClass: 'submitForApproval',
      width: '530px',
      height: '280px',
      disableClose: true,
    });
  }

  closeSubmitForApprovalDialog() {
    this.submitForApprovalMessage = '';
    this.dialog.closeAll();
  }

  submitForApproval(invalid: boolean) {
    if (!invalid) {
      const submissionRequest: SubmissionRequest = {
        summary: this.submitForApprovalMessage,
        fileName: this.selectedBotMenuData.id,
        entityId: this.sharedService.getEntityId(),
        entityType: this.sharedService.getEntityType().toUpperCase(),
        submissionType: 'PROMOTE',
      };

      this.submissionService
        .createSubmission(this.sharedService.getEnvironment(), submissionRequest)
        .subscribe((isSuccess) => {
          if (isSuccess) {
            swalMini({
              title: 'Successfully submitted for Approval',
              text: '',
              type: 'success',
            });
          } else {
            swalMini({
              title: 'A Problem occurred while submitting for Approval',
              text: '',
              type: 'warning',
            });
          }
          this.dialog.closeAll();
          this.submitForApprovalMessage = '';
        });
    }
  }

  myLockFlow(flowName: string) {
    const userDetails = this.sharedService.getUserInfo() as UserDetails;
    const lockFlowUserDetails = this.lockedFlows[flowName] as FlowUserDetails;
    if (lockFlowUserDetails && userDetails['id'] === lockFlowUserDetails.userId) {
      return true;
    }
    return false;
  }

  addPadding(level): string {
    return `${level * 16}px`;
  }

  setDataSource(dataSource) {
    // to fix the hax it need to subscribe to and the var needs to be send to the tree and then the search needs to change
    this.treeControl.dataNodes = dataSource;
  }

  checkSelectedFlow(file) {
    if (this.isModal && this.selectedFlowInModal) {
      return file.id === this.selectedFlowInModal.id;
    }
    return file.path === this.sharedService.getFlowPathName();
  }

  checkCanCreateNewFlow() {
    return (
      this.rolesService.canEditFlows() &&
      this.sharedService.getFlowPathName() === this.sharedService.getNewFlowName()
    );
  }

  checkFlowDeletePendingStatus(flowId) {
    const flow = find(this.flowsMetadata, { flowId });
    return flow && flow.hasOwnProperty('flowStatus') && flow.flowStatus === 'DELETE_PENDING';
  }

  private shouldFilterForApiFlow(): boolean {
    return !this.disableFlowFilter
            && FlowModeState.API_FLOW_MODE === this.stateService.getFlowMode();
  }

}
